<template>
  <div class="login-page">
    <!-- Background decorative elements -->
    <div class="bg-shape circle1"></div>
    <div class="bg-shape circle2"></div>
    <div class="bg-shape diamond1"></div>
    <div class="bg-shape hex1"></div>
    <div class="bg-shape square1"></div>
    <div class="bg-light-circle"></div>
    
    <div class="login-container">
      <div class="login-card">
        <!-- Logo and brand -->
        <div class="brand-section">
          <div class="logo">
            <svg width="72" height="33" viewBox="0 0 72 33" fill="none">
              <ellipse cx="36.3" cy="9.9" rx="11.7" ry="2.5" class="logo__path"></ellipse>
              <g>
                <ellipse cx="36.3" cy="23.5" rx="19.6" ry="2.5" class="logo__path"></ellipse>
                <path d="M72,16.3c0,1.2-10.1,2.1-31.9,2.1S0.6,17.5,0.6,16.3c0-1.2,17.7-2.1,39.5-2.1S72,15.1,72,16.3z" class="logo__path"></path>
              </g>
            </svg>
            <span class="logo__text">Cloudsway</span>
          </div>
        </div>

        <!-- Welcome text -->
        <div class="welcome-text">
          <h1>Welcome to Cloudsway</h1>
        </div>

        <!-- Login form -->
        <form class="login-form" @submit.prevent="handleLogin">
          <!-- Email input -->
          <div v-if="emailError" class="error-message">{{ emailError }}</div>
          <div class="input-group">
            <div class="input-wrapper">
              <svg class="input-icon" width="16" height="12" viewBox="0 0 16 12" fill="none">
                <path d="M2 1.5Q1.53125 1.53125 1.5 2L1.5 2.6875L6.90625 7.125Q7.40625 7.5 8 7.5Q8.625 7.5 9.125 7.125L14.5 2.6875L14.5 2Q14.4688 1.53125 14 1.5L2 1.5ZM1.5 4.625L1.5 10L1.5 4.625L1.5 10Q1.53125 10.4688 2 10.5L14 10.5Q14.4688 10.4688 14.5 10L14.5 4.625L10.0625 8.28125Q9.125 9 8 9Q6.875 9 5.9375 8.28125L1.5 4.625ZM0 2Q0.03125 1.15625 0.59375 0.59375Q1.15625 0.03125 2 0L14 0Q14.8438 0.03125 15.4062 0.59375Q15.9688 1.15625 16 2L16 10Q15.9688 10.8438 15.4062 11.4062Q14.8438 11.9688 14 12L2 12Q1.15625 11.9688 0.59375 11.4062Q0.03125 10.8438 0 10L0 2Z" fill="#6B7280"/>
              </svg>
              <input 
                type="email" 
                placeholder="Enter your email" 
                class="form-input" 
                v-model="formData.email"
                :class="{ 'error': emailError }"
                @blur="validateEmail"
                @input="onEmailChange"
                required
              >
            </div>
          </div>

          <!-- Verification code input -->
          <div class="input-group verification-group">
            <div class="input-wrapper">
              <svg class="input-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 0C8.2 0 8.4 0.1 8.5 0.2L14.8 2.7C15.2 2.9 15.5 3.4 15.5 3.9C15.5 5.5 15.4 7.7 14.8 9.8C14.2 12 12.7 14 10.2 15.3C9.3 15.7 8.7 15.7 7.8 15.3C5.3 14 3.8 12 3.2 9.8C2.6 7.7 2.5 5.5 2.5 3.9C2.5 3.4 2.8 2.9 3.2 2.7L9.5 0.2C9.6 0.1 9.8 0 8 0ZM8 2L8 14L8 2C5.7 3.1 4.4 4.8 3.9 6.6C3.4 8.4 3.4 10.1 8 14Z" fill="#6B7280"/>
              </svg>
              <input 
                type="text" 
                placeholder="Enter verification code" 
                class="form-input" 
                v-model="formData.verificationCode"
                required
              >
            </div>
            <button 
              type="button" 
              class="verification-btn" 
              @click="getVerificationCode"
              :disabled="countdown > 0"
            >
              {{ countdown > 0 ? `${countdown}s remaining` : 'Get Code' }}
            </button>
          </div>

          <!-- Hint message -->
          <div v-if="!hasRequestedCode" class="hint-message">
            <i class="fas fa-info-circle"></i>
            Please click "Get Code" to request verification code first
          </div>

          <!-- Login button -->
          <button 
            type="submit" 
            class="login-btn" 
            :disabled="loading || !hasRequestedCode"
            :title="!hasRequestedCode ? 'Please get verification code first' : ''"
          >
            {{ loading ? 'Signing in...' : 'Sign in / Sign up' }}
          </button>
        </form>

      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from 'axios'
import alert from '@/utils/alertStore'

const API_BASE_URL = process.env.NODE_ENV === 'development' ? '' : process.env.BACKEND_API_BASE_URL

export default {
  name: 'Login',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const loading = ref(false)
    const errorMessage = ref('')
    const emailError = ref('')
    const hasRequestedCode = ref(false)
    
    // Form data
    const formData = reactive({
      email: '',
      verificationCode: '',
      registration_token: '',
      customer_source: 4
    })

    // Get registration-token from URL parameters
    onMounted(() => {
      const registrationToken = route.query['registration-token'] || ''
      
      console.log('URL parameters parsing:', {
        registrationToken,
        allParams: route.query
      })
      
      if (registrationToken) {
        // Fix the issue where + signs in URL parameters become spaces
        formData.registration_token = registrationToken.replace(/ /g, '+')
        console.log('Set registration-token:', formData.registration_token)
      } else {
        console.warn('Missing registration-token parameter in URL')
      }
    })

    const countdown = ref(0)

    // Email format validation function
    const validateEmail = () => {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
      
      if (!formData.email) {
        emailError.value = ''
        return false
      }
      
      if (!emailRegex.test(formData.email)) {
        emailError.value = 'Please enter a valid email address'
        return false
      }
      
      emailError.value = ''
      return true
    }

    // Reset verification code request status when email input changes
    const onEmailChange = () => {
      hasRequestedCode.value = false
      countdown.value = 0
    }

    const getVerificationCode = async () => {
      if (!formData.email) {
        alert.warning('Please enter your email first', 'Email Required')
        return
      }
      
      if (!validateEmail()) {
        alert.error('Please enter a valid email address', 'Invalid Email Format')
        return
      }
      
      // If countdown is in progress, don't allow repeated sending
      if (countdown.value > 0) {
        return
      }
      
      try {
        console.log('Send verification code request:', { email: formData.email })
        
        const response = await axios.post(`${API_BASE_URL}/api/send-verification-code`, {
          email: formData.email
        })
        
        if (response.data.message === 'Verification code sent successfully') {
          console.log('Verification code sent successfully:', response.data)
          alert.success('Verification code sent! Please check your email', 'Sent Successfully')
          
          // Mark that verification code has been requested
          hasRequestedCode.value = true
          
          // Start countdown
          countdown.value = 60
          const timer = setInterval(() => {
            countdown.value--
            if (countdown.value <= 0) {
              clearInterval(timer)
            }
          }, 1000)
        } else {
          alert.error(response.data.error || 'Failed to send verification code', 'Send Failed')
        }
      } catch (error) {
        console.error('Failed to send verification code:', error)
        alert.error(error.response?.data?.error || 'Failed to send verification code, please try again', 'Send Failed')
      }
    }

    // Login logic - first login, if 404 then register, then login again
    const handleLogin = async () => {
      console.log('Start login process')
      
      if (!formData.email || !formData.verificationCode) {
        alert.warning('Please fill in email and verification code', 'Incomplete Information')
        return
      }
      
      if (!validateEmail()) {
        alert.error('Please enter a valid email address', 'Invalid Email Format')
        return
      }
      
      if (!hasRequestedCode.value) {
        alert.warning('Please click "Get Code" button first', 'Verification Code Not Requested')
        return
      }
      
      loading.value = true
      
      const loginData = {
        email: formData.email,
        verification_code: formData.verificationCode,
        registration_token: formData.registration_token,
        customer_source: formData.customer_source
      }
      
      try {
        console.log('Send login request:', loginData)
        
        // 1. First try to login
        const loginRes = await axios.post(`${API_BASE_URL}/api/login`, loginData)
        
        console.log('Login response complete data:', loginRes.data)
        
        // Check for multiple possible successful response formats
        if (loginRes.data.code === 200 || loginRes.data.data?.message === 'User exists' || loginRes.data.message === 'login success') {
          console.log('Login successful', loginRes.data)
          
          const responseData = loginRes.data.data || loginRes.data
          const userInfo = {
            email: responseData.login_name || responseData.email || formData.email,
            name: (responseData.login_name || responseData.email || formData.email).split('@')[0].charAt(0).toUpperCase() + (responseData.login_name || responseData.email || formData.email).split('@')[0].slice(1),
            customer_code: responseData.customer_code,
            token: responseData.token,
            registration_token: formData.registration_token
          }
          localStorage.setItem('userInfo', JSON.stringify(userInfo))
          
          // Set console_cs_token cookie
          if (responseData.token) {
            document.cookie = `console_cs_token=${responseData.token}; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Lax`
            console.log('Cookie set: console_cs_token')
          }
          
          console.log('User info saved:', userInfo)
          router.push('/search')
          return
        }
        
      } catch (loginErr) {
        console.log('Login request failed:', loginErr.response?.status, loginErr.response?.data)
        
        // 2. If login returns 404, it means the user does not exist
        if (loginErr.response?.status === 404) {
          // Check if registration-token exists, if not, do not allow registration
          if (!formData.registration_token) {
            console.log('User not found and no registration-token, registration not allowed')
            alert.error('User not found. Please make a jump from marketplace.', 'User Not Found')
            return
          }
          
          console.log('User not found, start registration process')
          
          try {
            // Prepare registration data
            const registerData = { ...loginData }
            
            console.log('Send registration request:', registerData)
            const regRes = await axios.post(`${API_BASE_URL}/api/register`, registerData)
            
            if (regRes.data.code === 0) {
              console.log('Registration successful, try login again')
              alert.success('Account created successfully! Logging you in...', 'Registration Successful')
              
              // 3. After successful registration, try to login again
              const loginRes2 = await axios.post(`${API_BASE_URL}/api/login`, loginData)
              
              if (loginRes2.data.code === 200 || loginRes2.data.data?.message === 'User exists' || loginRes2.data.message === 'login success') {
                console.log('Login successful after registration', loginRes2.data)
                
                const responseData = loginRes2.data.data || loginRes2.data
                const userInfo = {
                  email: responseData.login_name || responseData.email || formData.email,
                  name: (responseData.login_name || responseData.email || formData.email).split('@')[0].charAt(0).toUpperCase() + (responseData.login_name || responseData.email || formData.email).split('@')[0].slice(1),
                  customer_code: responseData.customer_code,
                  token: responseData.token,
                  registration_token: formData.registration_token
                }
                localStorage.setItem('userInfo', JSON.stringify(userInfo))
                
                // Set console_cs_token cookie
                if (responseData.token) {
                  document.cookie = `console_cs_token=${responseData.token}; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Lax`
                  console.log('Cookie set: console_cs_token')
                }
                
                console.log('User info saved:', userInfo)
                router.push('/search')
                return
              } else {
                alert.error('Login failed after registration, please try again', 'Login Failed')
              }
            } else {
              alert.error(regRes.data.message || 'Registration failed, please check your information', 'Registration Failed')
            }
            
          } catch (regErr) {
            console.error('Registration failed:', regErr)
            alert.error(regErr.response?.data?.message || 'Registration failed, please try again', 'Registration Failed')
          }
        } else {
          // Other login errors
          console.error('Login failed:', loginErr)
          alert.error(loginErr.response?.data?.message || 'Login failed, please check your network or try again later', 'Login Failed')
        }
      } finally {
        loading.value = false
      }
    }

    return {
      formData,
      loading,
      countdown,
      emailError,
      hasRequestedCode,
      validateEmail,
      onEmailChange,
      getVerificationCode,
      handleLogin
    }
  }
}
</script>

<style lang="scss" scoped>
.login-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #ffffff 0%, #f0f8ff 15%, #e6f3ff 30%, #cce7ff 45%, #99d6ff 60%, #66c2ff 75%, #3399ff 90%, #0066cc 100%);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Background decorative circles */
.bg-shape {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
}

.circle1 {
  width: 300px;
  height: 300px;
  background: rgba(255, 255, 255, 0.1);
  top: -150px;
  left: -150px;
  animation: float 8s ease-in-out infinite;
}

.circle2 {
  width: 200px;
  height: 200px;
  background: rgba(255, 255, 255, 0.08);
  top: 20%;
  right: -100px;
  animation: float 10s ease-in-out infinite reverse;
}

.diamond1 {
  width: 150px;
  height: 150px;
  background: rgba(255, 255, 255, 0.06);
  bottom: -75px;
  left: 20%;
  animation: float 12s ease-in-out infinite;
}

.hex1 {
  width: 180px;
  height: 180px;
  background: rgba(255, 255, 255, 0.05);
  bottom: 10%;
  right: -90px;
  animation: float 9s ease-in-out infinite reverse;
}

.square1 {
  width: 120px;
  height: 120px;
  background: rgba(255, 255, 255, 0.07);
  top: 60%;
  left: -60px;
  animation: float 11s ease-in-out infinite;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) scale(1);
  }
  50% {
    transform: translateY(-30px) scale(1.05);
  }
}

.bg-light-circle {
  position: absolute;
  width: 350px;
  height: 350px;
  background: radial-gradient(circle at center, 
    rgba(255, 255, 255, 0.9) 0%, 
    rgba(255, 255, 255, 0.7) 40%, 
    rgba(255, 255, 255, 0.3) 70%, 
    transparent 100%);
  border-radius: 50%;
  top: -150px;
  left: -150px;
  animation: lightFloat 20s ease-in-out infinite;
  pointer-events: none;
  z-index: 5;
  box-shadow: 0 0 60px rgba(255, 255, 255, 0.3);
}

@keyframes lightFloat {
  0%, 100% {
    transform: translate(0px, 0px) scale(1);
    opacity: 0.8;
  }
  25% {
    transform: translate(80px, -40px) scale(1.1);
    opacity: 1;
  }
  50% {
    transform: translate(-60px, 60px) scale(0.9);
    opacity: 0.6;
  }
  75% {
    transform: translate(40px, 80px) scale(1.05);
    opacity: 0.9;
  }
}

.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 20px;
}

.login-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 48px 40px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  z-index: 10;
  transition: all 0.15s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transform: translateY(0);
  
  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 28px 56px rgba(0, 0, 0, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
  }
}

.brand-section {
  text-align: center;
  margin-bottom: 32px;
}

.logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #2563EB;
}

.logo__path {
  fill: currentColor;
}

.logo__text {
  font-family: 'JetBrains Mono', 'Monaco', 'Consolas', monospace;
  font-size: 32px;
  font-weight: 500;
  line-height: 32px;
}

.welcome-text {
  text-align: center;
  margin-bottom: 32px;
}

.welcome-text h1 {
  font-size: 24px;
  font-weight: 500;
  color: #1f2937;
  margin: 0;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 24px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.verification-group {
  flex-direction: row;
  align-items: stretch;
  gap: 12px;
}

.input-wrapper {
  position: relative;
  flex: 1;
}

.input-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
}

.form-input {
  width: 100%;
  height: 48px;
  padding: 0 16px 0 48px;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  font-size: 16px;
  background: #ffffff;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #2563EB;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  &.error {
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.error-message {
  color: #dc2626;
  font-size: 14px;
  margin-bottom: 8px;
  padding-left: 4px;
}

.hint-message {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #3399ff;
  font-size: 14px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #f0f8ff;
  border: 1px solid #cce7ff;
  border-radius: 8px;
  
  i {
    font-size: 16px;
  }
}

.verification-btn {
  padding: 0 20px;
  height: 48px;
  background: #f3f4f6;
  border: 1px dashed #3399ff;
  border-radius: 12px;
  font-size: 14px;
  color: #3399ff;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: #e5e7eb;
    border: 1px solid #3399ff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    color: #9ca3af;
    border-color: #d1d5db;
  }
}

.login-btn {
  width: 100%;
  height: 48px;
  background: #3399ff;
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #2d89ef;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(51, 153, 255, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
}


/* Responsive design */
@media (max-width: 480px) {
  .login-card {
    padding: 32px 24px;
    margin: 20px;
    border-radius: 16px;
  }

  .verification-group {
    flex-direction: column;
    gap: 12px;
  }

  .verification-btn {
    width: 100%;
  }

}

</style>